import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { Conversation, ConversationId, ConversationMemberRef } from '@cyphex/types';
import { chatApi } from '../api';
import { CHAT_KEYS } from '../constants';
import {
  completeMention,
  mentionTargetsOf,
  mentionText,
  openMentionAt,
  rankTargets,
  type MentionTarget,
} from '../lib/markdown/mentions';
import { useMe } from './useMe';

/**
 * For the SEND path, which runs outside the composer: whatever roster the cache
 * holds. The picker fetches the full roster the moment an `@` is typed, so it is
 * there by the time anything with an `@` in it is sent; the conversation's own
 * (capped) member list is the fallback.
 */
export function cachedMentionTargets(
  client: QueryClient,
  conversationId: ConversationId,
  meId: string | null,
): MentionTarget[] {
  const roster =
    client.getQueryData<ConversationMemberRef[]>(CHAT_KEYS.members(conversationId)) ??
    client.getQueryData<Conversation>(CHAT_KEYS.conversation(conversationId))?.members ??
    [];
  return mentionTargetsOf(roster, meId);
}

export interface MentionPickerState {
  /** The rows to render, already ranked. Empty means hidden. */
  suggestions: MentionTarget[];
  activeIndex: number;
  /** True while the popover is up and owns the arrow / Enter / Tab / Esc keys. */
  isOpen: boolean;
  move: (delta: number) => void;
  setActive: (index: number) => void;
  /** The draft and caret after taking the highlighted (or given) person. */
  accept: (target?: MentionTarget) => { text: string; caret: number } | null;
  close: () => void;
}

const ROSTER_STALE_MS = 60_000;

/**
 * `@` autocomplete for the chat composer - the people picker it never had.
 *
 * The roster is fetched only once an `@` is in the box, so opening a chat costs
 * nothing extra. Escape dismisses THIS mention (keyed by where its `@` sits),
 * not the feature. And a name typed out in full closes the list by itself, so
 * Enter sends the message instead of "accepting" what is already written.
 */
export function useMentionPicker(
  conversationId: ConversationId | null,
  text: string,
  caret: number,
  enabled: boolean,
): MentionPickerState {
  /* The chat feature's own "me" - the identity every member list in here is
     compared against. */
  const meId = useMe()?.id ?? null;
  const armed = enabled && conversationId !== null && text.includes('@');

  const roster = useQuery({
    queryKey: CHAT_KEYS.members(conversationId ?? ('' as ConversationId)),
    queryFn: () => chatApi.listMembers(conversationId as ConversationId),
    enabled: armed,
    staleTime: ROSTER_STALE_MS,
  });

  const targets = useMemo(() => mentionTargetsOf(roster.data ?? [], meId), [meId, roster.data]);
  const typing = useMemo(
    () => (armed ? openMentionAt(text, caret, targets) : null),
    [armed, caret, targets, text],
  );
  const typingStart = typing?.start ?? null;
  const typingQuery = typing?.query ?? null;

  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const suggestions = useMemo(() => {
    if (typingQuery === null || typingStart === dismissedAt) return [];
    const ranked = rankTargets(targets, typingQuery);
    const only = ranked.length === 1 ? ranked[0] : undefined;
    // Already written out in full: nothing left to complete.
    if (only !== undefined && mentionText(only).toLowerCase() === typingQuery.toLowerCase()) return [];
    return ranked;
  }, [dismissedAt, targets, typingQuery, typingStart]);

  // A narrower list must put the highlight back on its best match.
  useEffect(() => {
    setActiveIndex(0);
  }, [typingQuery]);

  // Leaving mention-mode re-arms it, so Escape dismisses one attempt only.
  useEffect(() => {
    if (typingStart === null) setDismissedAt(null);
  }, [typingStart]);

  const move = useCallback(
    (delta: number) => {
      setActiveIndex((current) => {
        const count = suggestions.length;
        return count === 0 ? 0 : (current + delta + count) % count;
      });
    },
    [suggestions.length],
  );

  const accept = useCallback(
    (chosen?: MentionTarget): { text: string; caret: number } | null => {
      const target = chosen ?? suggestions[activeIndex];
      if (target === undefined || typingStart === null) return null;
      // The trailing space closes the list and puts the caret where the message continues.
      return completeMention(text, typingStart, caret, target);
    },
    [activeIndex, caret, suggestions, text, typingStart],
  );

  const close = useCallback(() => {
    setDismissedAt(typingStart);
  }, [typingStart]);

  return {
    suggestions,
    activeIndex,
    isOpen: suggestions.length > 0,
    move,
    setActive: setActiveIndex,
    accept,
    close,
  };
}
